import { useCallback, useRef, useState } from "react"
import { LexicalText, Task } from "@/schema"
import { cn } from "@ui/utils"
import { toast } from "sonner"

import { useDebounce } from "@/hooks/use-debounce"
import { EditorRefHandle } from "@/components/lexical"
import TaskEditor from "@/components/tasks/task-editor"

interface TaskDescriptionProps {
  task: Task
  isCompleted: boolean
  descriptionEditorRef?: React.RefObject<EditorRefHandle | null>
  onUpdateTask?: (payload: Partial<Task> & { id: string }, options?: { onSuccess?: () => void }) => void | Promise<void>
  readOnly?: boolean
}

export default function TaskDescription({
  task,
  isCompleted,
  descriptionEditorRef,
  onUpdateTask,
  readOnly = false,
}: TaskDescriptionProps) {
  const [, setCurrentDescription] = useState<LexicalText | undefined>(task.description)
  const localEditorRef = useRef<EditorRefHandle>(null)

  // Use the provided ref if available, otherwise use the local one
  const editorRef = descriptionEditorRef || localEditorRef

  const saveDescription = useCallback(
    (description?: LexicalText) => {
      if (!description || !task.id) return

      const updatePayload = {
        id: task.id,
        description,
      } as Partial<Task> & { id: string }

      onUpdateTask?.(updatePayload, {
        onSuccess: () => {
          toast.success("Task description updated.")
        },
      })
    },
    [task.id, onUpdateTask]
  )

  const debouncedUpdateTask = useDebounce((description: LexicalText) => {
    saveDescription(description)
  }, 1000)

  const handleChange = useCallback(
    (lexicalText: LexicalText) => {
      setCurrentDescription(lexicalText)
      debouncedUpdateTask(lexicalText)
    },
    [debouncedUpdateTask]
  )

  return (
    <div className="my-3">
      <div
        className={cn(
          "relative max-h-72 overflow-y-auto rounded-lg border border-transparent px-2 py-1",
          isCompleted ? "text-muted-foreground" : "hover:border-border focus-within:border-border"
        )}
      >
        <TaskEditor
          ref={editorRef}
          onChange={(lexicalText) => handleChange(lexicalText)}
          initialDescriptionContent={task.description}
          show={{ title: false, description: true }}
          descriptionPlaceholder="Add a description..."
          styleOverrides={{
            "--lexical-editor-min-height": "24px",
            "--lexical-editor-font-size": "15px",
          }}
          editable={!readOnly}
        />
      </div>
    </div>
  )
}
